// Razorpay Checkout helper.
//
// Lazy-loads the Razorpay checkout.js SDK on first use, then opens the
// hosted checkout modal with a Promise-based API:
//
//   const result = await openRazorpayCheckout({ order, name, prefill, ... });
//   // -> { razorpay_order_id, razorpay_payment_id, razorpay_signature }
//
// Rejects with { code: "dismissed" } if the user closes the modal, or
// { code: "failed", reason } if Razorpay's failure callback fires.

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
let sdkPromise = null;

/**
 * Load the Razorpay Checkout script once and cache the promise so repeat
 * calls don't inject the tag twice.
 */
export function loadRazorpaySdk() {
    if (typeof window === "undefined") {
        return Promise.reject(new Error("Not running in browser"));
    }
    if (window.Razorpay) return Promise.resolve(window.Razorpay);
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = CHECKOUT_SRC;
        s.async = true;
        s.onload = () => {
            if (window.Razorpay) resolve(window.Razorpay);
            else reject(new Error("Razorpay SDK failed to initialize"));
        };
        s.onerror = () => {
            sdkPromise = null;
            reject(new Error("Failed to load Razorpay checkout script"));
        };
        document.body.appendChild(s);
    });
    return sdkPromise;
}

/**
 * Open the Razorpay Checkout modal.
 *
 * @param {Object}  args
 * @param {Object}  args.order            server /checkout response
 *                                        ({order_id, amount, currency, key_id, plan_name, user_email, user_name})
 * @param {string}  [args.name]           storefront name (default: NaukriDaddy)
 * @param {string}  [args.description]    line-item description
 * @param {string}  [args.themeColor]     hex accent
 * @param {Object}  [args.prefill]        overrides for {name, email, contact}
 * @param {Object}  [args.notes]          extra notes attached to the payment
 * @returns {Promise<{razorpay_order_id, razorpay_payment_id, razorpay_signature}>}
 */
export async function openRazorpayCheckout({
    order,
    name = "NaukriDaddy",
    description = "",
    themeColor = "#1E3AEF",
    prefill = {},
    notes = {},
} = {}) {
    if (!order || !order.order_id || !order.key_id) {
        throw new Error("Missing order details from server");
    }
    const Razorpay = await loadRazorpaySdk();
    return new Promise((resolve, reject) => {
        const rzp = new Razorpay({
            key: order.key_id,
            amount: order.amount,          // paise, from server
            currency: order.currency || "INR",
            order_id: order.order_id,
            name,
            description: description || order.plan_name || "Payment",
            image: "/logo192.png",
            prefill: {
                name: prefill.name || order.user_name || "",
                email: prefill.email || order.user_email || "",
                contact: prefill.contact || "",
            },
            notes,
            theme: { color: themeColor },
            modal: {
                ondismiss: () => reject({ code: "dismissed" }),
                escape: true,
                backdropclose: false,
            },
            handler: (resp) => {
                // resp = { razorpay_order_id, razorpay_payment_id, razorpay_signature }
                resolve(resp);
            },
        });
        rzp.on("payment.failed", (resp) => {
            reject({
                code: "failed",
                reason: resp?.error?.description || "Payment failed",
                raw: resp,
            });
        });
        rzp.open();
    });
}
